import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
// Only used by the commented-out "Psychoeducation Session Required?" block below.
// import { Checkbox, FormControlLabel } from "@mui/material";
// import Text from "../../../../../../../../../components/Text";
// import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import { PsychiatricEvolutionForm } from "../components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionForm";
import RiskAssessmentModal from "../components/form-components/InitialPsychotherapy/modals/RiskAssessmentModal";
import PrimaryCareSoapNotePlan from "../components/form-components/primary-note/PrimaryCareSoapNotePlan";
import CustomSelect from "../../../../../../../../../components/CustomSelect";
import ModalitySpecificComponents from "./ModalitySpecificComponents";
import MentalStatusExaminationModal from "../components/form-components/InitialPsychotherapy/modals/MentalStatusExamination";
import {
  RiskAssessmentSummary,
  SelectedSectionsSummary,
} from "../components/form-components/InitialPsychotherapy/SelectedFormSummary";
import { PSYCHIATRIC_HISTORY_SECTIONS } from "../components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionFormConstant";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";
import CbtInterventionsProvided from "./CbtInterventionsProvided";
import PsychodynamicTherapyForm from "./therapy-modality-forms/PsychodynamicTherapyForm";
import TraumaFocusedTherapyForm from "./therapy-modality-forms/TraumaFocusedTherapyForm";
import SolutionFocusedTherapyForm from "./therapy-modality-forms/SolutionFocusedTherapyForm";

const NEW_THERAPY_MODALITIES = new Set([
  "Psychodynamic_Therapy",
  "Trauma_Therapy",
  "Solution_Focused_Therapy",
]);

// Clinical Assessment - Mental Status Examination options
const PRIMARY_MODALITY_OPTIONS = [
  {
    value: "Cognitive_Behavioral_Therapy_CBT",
    label: "Cognitive Behavioral Therapy (CBT)",
  },
  {
    value: "Dialectical_Behavior_Therapy_DBT",
    label: "Dialectical Behavior Therapy (DBT)",
  },
  {
    value: "Acceptance_Commitment_Therapy_ACT",
    label: "Acceptance & Commitment Therapy (ACT)",
  },
  // { value: "Psychodynamic_Therapy", label: "Psychodynamic Therapy" },
  // { value: "Trauma_Therapy", label: "Trauma Therapy" },
  // { value: "Solution_Focused_Therapy", label: "Solution-Focused Therapy" },
];
interface PsychotherapyProps {
  formik: FormikProps<Form2Payload>;
  // Owned by PatientEncounter: archives the saved encounter summary and resets
  // the form before landing on the newly picked modality.
  onModalityChange?: (modality: string) => void;
}

const Psychotherapy = ({ formik, onModalityChange }: PsychotherapyProps) => {
  const [icdDisclosure] = useReduceDisclosure(3);
  const mseDisclosure = useDisclosure();
  const psychiatricEvolutionDisclosure = useDisclosure();
  const riskAssessmentDisclosure = useDisclosure();
  const appointmentDisclosure = useDisclosure();

  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  const isNewModality = NEW_THERAPY_MODALITIES.has(
    formik.values?.primaryModality ?? ""
  );

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Session Information / Primary Modality Dropdown */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Session Information</CustomTitle>
        </Flex>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomSelect
            name="primaryModality"
            value={formik.values?.primaryModality ?? ""}
            options={PRIMARY_MODALITY_OPTIONS}
            placeholder="Select Primary Modality"
            onChange={(v) =>
              onModalityChange
                ? onModalityChange(v as string)
                : formik.setFieldValue("primaryModality", v)
            }
          />
        </Grid>

        {/* Subjective section  */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle color={"primary"}>Subjective</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Chief Complaint"
            rows={3}
            name="subjectiveChiefComplaint"
            placeholder="Enter Chief Complaint"
            onBlur={formik.handleBlur}
            value={formik.values?.subjectiveChiefComplaint}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid size={12}>
          <CustomTextArea
            label="HPI"
            rows={3}
            name="historyOfPresentIllness"
            placeholder="Enter History of Present Illness"
            onBlur={formik.handleBlur}
            value={formik.values?.historyOfPresentIllness}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* psychiatric evalution section  */}
        {/* this field have independent API to save the data */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Psychiatric Evaluation</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => psychiatricEvolutionDisclosure.open()}
            label="Add Psychiatric Evaluation"
          />
          <PsychiatricEvolutionForm
            disclosureHost={psychiatricEvolutionDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PSYCHIATRIC_HISTORY_SECTIONS}
          />
        </Grid>

        {/* Objective - Mental Status Examination (MSE) */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Objective</CustomTitle>
        </Flex>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Mental Status Examination (MSE)</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => mseDisclosure.open()}
            label="Add Mental Status Examination (MSE)"
          />
          <MentalStatusExaminationModal
            disclosureHost={mseDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PHYSICAL_ASSESSMENT_EXAM_SECTIONS}
            overallNoteKey="overallMseNote"
            overallNoteLabel="Overall MSE Note"
          />
        </Grid>

        {/* Render modality-specific full forms */}
        {formik.values?.primaryModality === "Psychodynamic_Therapy" && (
          <PsychodynamicTherapyForm formik={formik} />
        )}
        {formik.values?.primaryModality === "Trauma_Therapy" && (
          <TraumaFocusedTherapyForm formik={formik} />
        )}
        {formik.values?.primaryModality === "Solution_Focused_Therapy" && (
          <SolutionFocusedTherapyForm formik={formik} />
        )}

        {/* Existing form sections for CBT/DBT/ACT and default */}
        {!isNewModality && (
          <>
            {/* Presenting Concerns & Session Focus */}
            <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
              <CustomTitle>Presenting Concerns & Session Focus</CustomTitle>
            </Flex>
            <Grid size={12}>
              <CustomTextArea
                label="Presenting Concerns"
                rows={3}
                name="presentingConcerns"
                placeholder="Current symptoms, stressors, or concerns discussed"
                onBlur={formik.handleBlur}
                value={formik.values?.presentingConcerns ?? ""}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid size={12}>
              <CustomTextArea
                label="Session Focus/Agenda"
                rows={3}
                name="sessionFocus"
                placeholder="Main topics addressed in this session"
                onBlur={formik.handleBlur}
                value={formik.values?.sessionFocus ?? ""}
                onChange={formik.handleChange}
              />
            </Grid>

            {/* modality specific assessment */}
            {formik.values?.primaryModality && (
              <ModalitySpecificComponents
                primaryModality={formik.values?.primaryModality ?? ""}
                formik={formik}
              />
            )}

            {/* Interventions & Client Response (CBT only) */}
            {formik.values?.primaryModality ===
              "Cognitive_Behavioral_Therapy_CBT" && (
              <>
                <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
                  <CustomTitle>Interventions & Client Response</CustomTitle>
                </Flex>
                <CbtInterventionsProvided formik={formik} />
              </>
            )}

            {/* Homework & Progress (CBT / ACT) */}
            {(formik.values?.primaryModality ===
              "Cognitive_Behavioral_Therapy_CBT" ||
              formik.values?.primaryModality ===
                "Acceptance_Commitment_Therapy_ACT") && (
              <>
                <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
                  <CustomTitle>Homework & Progress</CustomTitle>
                </Flex>
                <Grid size={12}>
                  {(() => {
                    // ACT stores homework at psychotherapyActVisitNote.homework;
                    // CBT stores it at psychotherapyCbtVisitNote.homeworkSection.
                    const isAct =
                      formik.values?.primaryModality ===
                      "Acceptance_Commitment_Therapy_ACT";
                    const homeworkPath = isAct
                      ? "psychotherapyActVisitNote.homework"
                      : "psychotherapyCbtVisitNote.homeworkSection";
                    const homeworkValue = isAct
                      ? formik.values?.psychotherapyActVisitNote?.homework
                      : formik.values?.psychotherapyCbtVisitNote
                          ?.homeworkSection;
                    return (
                      <CustomTextArea
                        label="Homework Assigned"
                        rows={3}
                        placeholder="Between-session assignments and practice exercises"
                        value={homeworkValue ?? ""}
                        onChange={(e) =>
                          formik.setFieldValue(homeworkPath, e.target.value)
                        }
                      />
                    );
                  })()}
                </Grid>
              </>
            )}
          </>
        )}

        {/* ===== Assessment section (same as IPE template) ===== */}
        <Flex flexDirection="column" width={"100%"} gap={1} ml={2}>
          <CustomTitle my={1} color={"primary"}>
            Assessment
          </CustomTitle>
          <CustomTextArea
            rows={4}
            variant="outlined"
            name="assessmentFreeNote"
            placeholder="Assessment..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentFreeNote || ""}
            onChange={formik.handleChange}
          />

          <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <StyleAddButton
              w="120px"
              onClick={() => icdDisclosure.open()}
              label="Add ICD Codes"
            />
            <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
          </Flex>
          {!!formik.values?.assessmentCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1}>
              {renderSelectedIcdCodes(
                formik.values?.assessmentCodes ?? [],
                (code) => {
                  formik.setFieldValue("assessmentCodes", [
                    ...(formik.values?.assessmentCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
        </Flex>

        {/* Risk assessment section */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Risk Assessment:</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => riskAssessmentDisclosure.open()}
            label="Add Risk Assessment"
          />
          <RiskAssessmentModal
            disclosureHost={riskAssessmentDisclosure}
            formik={formik}
          />
          <RiskAssessmentSummary formik={formik} />
        </Grid>

        {/* ===== Plan section (same as IPE template) ===== */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Plan
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Plan Note"
            rows={3}
            name="planFreeNote"
            placeholder="Plan..."
            onBlur={formik.handleBlur}
            value={formik.values?.planFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <PrimaryCareSoapNotePlan
            formik={formik}
            showSendAssessment
            showAssignEducation
            showTherapies
          />
        </Grid>

        {/* Orders, Prescriptions, Lab requisition, CPT and Therapies are all
            rendered inside <PrimaryCareSoapNotePlan/> above. The standalone
            duplicate block was removed (ECRH-5279). */}

        {/* Follow Up & Instructions section */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Follow Up & Instructions
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Follow Up & Instructions"
            rows={3}
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {hasUpdateAppointment && (
          <Grid size={12}>
            <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <StyleAddButton
                w="200px"
                onClick={() => {
                  appointmentDisclosure.open();
                }}
                label="Schedule F/U Appointment"
              />
              {appointmentDisclosure.opened && (
                <NewAppointment
                  enablePrefillPatientData
                  opened={appointmentDisclosure.opened}
                  onClose={appointmentDisclosure.close}
                />
              )}
            </Flex>
          </Grid>
        )}

        {/* "Psychoeducation Session Required?" checkbox and the form it gated are
            not applicable to the Psychotherapy note type. Kept for discoverability.
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  formik.setFieldValue(
                    "psychoeducationSessionRequired",
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid item xs={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
        */}
      </Grid>
    </Flex>
  );
};

export default Psychotherapy;
